<script>
import {defineComponent} from "vue";
import {useDonationSessionStore} from "../../stores/donationSession";

import {useI18n} from "vue-i18n";
import {ApplicationHelper} from "../../helpers/ApplicationHelper";
import {useLandingPageStore} from "../../stores/landingPage";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import Tooltip from "../components/tooltip.vue";
import ToggleSwitch from "../components/inputs/ToggleSwitch.vue";


export default defineComponent({
  // Not named the best, adding this button will add the whole gift basket / checkout process drawer
  name: "DonationCheckoutTotal",
  components: {
    ToggleSwitch,
    Tooltip,
    VueDatePicker
  },

  setup() {
    const {t} = useI18n();

    const sessionStore = useDonationSessionStore();
    const landingStore = useLandingPageStore();

    return {
      t,
      sessionStore,
      landingStore,
    };
  },

  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    formData() {
      return this.sessionStore.donationForm;
    },

    hasDonationInCart() {
      return this.sessionStore.donationCart.length > 0 || this.sessionStore.immediateCheckoutDonation;
    },


    isSubscriptionCheckout() {
      return this.sessionStore.immediateCheckoutDonation && this.sessionStore.immediateCheckoutDonation.type === 'RecurringDonation';
    },
    showDelayedSubscriptionToggle() {
      return this.sessionStore.orgProcessor
        && this.sessionStore.orgProcessor.delayed_subscriptions_enabled
        && this.isSubscriptionCheckout;
    },

    // Determine the max date to display in delay donation date picker, based on the selected frequency
    // The user can specify any date up to 6 months in the future to delay the subscription start
    calendarMax() {
      let today = new Date();
      let futureDate = new Date();
      futureDate.setMonth(today.getMonth() + 6);
      return futureDate;
    },

    // Date must be in the future for delayed donations
    calendarMin() {
      let today = new Date();
      let tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      return tomorrow;
    },
    formErrors() {
      return this.sessionStore.donationForm.errors;
    },
    // Only show the donor wall toggle if any items in the cart have it enabled
    showDonorWallToggle() {
      console.log('showDonorWallToggle', this.sessionStore.donationCart);
      return this.sessionStore.donationCart.some(item => item.selectedTarget?.donor_wall_enabled);
    }
  },
  watch: {},
});

</script>

<template>
  <div>
    <!-- Create account toggle -->
    <div class="toggle-container">
      <toggle-switch
        v-model="formData.createAccountOnSuccess"
        class="toggle-switch"
        :background-color="landingStore.localBrand.color_primary_dark"
      />
      <div class="body-1-regular ml-2">
        <span>I would like to track my giving to {{ sessionStore.currentOrganization.name }} by creating a giving account</span>
        <tooltip
          class="ml-2"
          message="Once your gift is complete, check your email to finish setting up your account and manage your donations."
          color="#3a4473"
        />
      </div>
    </div>
    <!--"Instructions will be sent to your email after your gift is processed to finish your account registration.
                  You will be able to log into your giving account to view and manage all of your gifts"-->

    <!-- Public gift toggle -->
    <div
      v-if="showDonorWallToggle"
      class="toggle-container mt-4"
    >
      <toggle-switch
        v-model="formData.donationPublic"
        class="toggle-switch"
        :background-color="landingStore.localBrand.color_primary_dark"
      />
      <div class="body-1-regular ml-2">
        <span>I would like to display my name publicly for my gift</span>
        <tooltip
          class="ml-2"
          message="Selecting this option allows your name to be displayed on the campaign.
                   If unchecked, your donation will remain anonymous"
          color="#3a4473"
        />
      </div>
      <!--"By checking this box, you may have your name appear publicly on the fundraiser or campaign to which you are giving.
                          Only your name will appear publicly. The Owner of the organization may choose not to display the names of the donors that are giving to this cause.
                          By not checking this, your gift will remain anonymous on any public pages associated with this cause or organization."-->
    </div>
  </div>
  <div
    class="w-full flex flex-col bg-gray-100 p-4 my-4 border border-slate-300 rounded-md text-black"
  >
    <div class="flex flex-row mb-4 w-full cursor-pointer">
      <!-- Only show the logo if no tip option, don't want to show it twice -->
      <img
        v-if="!sessionStore.showTipOption"
        src="@/assets/logos/allgive_thumbnail.png"
        class="w-[40px] h-[40px] mr-2"
        alt="AllGive logo"
      >
      <div class="text-xl font-semibold mt-1">
        Giving Summary
      </div>
    </div>
    <div class="w-full">
      <ul>
        <li v-for="donation in sessionStore.donationCartLineItems">
          <div class="flex flex-row font-semibold justify-between pb-2">
            <div class="text-md">
              {{ donation.name }}
            </div>

            <div class="self-end">
              <span class="font-bold text-lg">
                {{ ApplicationHelper.formatMoney(donation.amount) }}
              </span>
            </div>
          </div>
        </li>

        <li>
          <div
            v-if="sessionStore.cartTipAmount"
            class="flex flex-row justify-between pb-2"
          >
            <div class="text-md font-semibold text-default_primary_dark">
              Platform Contribution
            </div>
            <div class="font-bold text-lg text-default_primary_dark">
              {{ ApplicationHelper.formatMoney(sessionStore.cartTipAmount) }}
            </div>
          </div>
        </li>
      </ul>
    </div>


    <div class="flex flex-row w-full justify-between mt-2">
      <div class="text-lg font-semibold">
        Total
      </div>
      <div class="font-bold text-xl">
        <span>{{ ApplicationHelper.formatMoney(sessionStore.cartTotal) }}</span>
        <span class="text-sm ml-2 text-gray-600"> {{ sessionStore.currentOrganization.currency }}</span>
        <span
          v-if="isSubscriptionCheckout"
          class="text-xs font-bold text-gray-600 capitalize"
        >
          / {{ sessionStore.immediateCheckoutDonation.frequency }}
        </span>
      </div>
    </div>

    <div
      v-if="isSubscriptionCheckout"
    >
      <div
        class="text-xs p-2"
      >
        <div v-if="sessionStore.immediateCheckoutDonation.frequency === 'monthly'">
          {{ t('donation.frequencyDetails.month') }}
        </div>
        <div v-if="sessionStore.immediateCheckoutDonation.frequency === 'quarterly'">
          {{ t('donation.frequencyDetails.quarter') }}
        </div>
        <div v-if="sessionStore.immediateCheckoutDonation.frequency === 'weekly'">
          {{ t('donation.frequencyDetails.week') }}
        </div>
        <div v-if="sessionStore.immediateCheckoutDonation.frequency === 'biweekly'">
          {{ t('donation.frequencyDetails.biWeek') }}
        </div>
      </div>
    </div>

    <div
      v-if="showDelayedSubscriptionToggle"
      class="mt-2"
    >
      <div class="flex flex-row">
        <div
          class="mr-1"
        >
          <input
            v-model="sessionStore.donationForm.delayStart"
            type="checkbox"
          >
        </div>
        <label> Delay Giving Start Date</label>
        <tooltip
          class="ml-2"
          variant="question"
          size="12px"
        >
          <template #message-slot>
            <div>You can choose to delay the the start of your gift by up to 6 months.</div>
            <div class="mt-2">
              You will not be charged until the selected date.
            </div>

            <div class="mt-2">
              Delaying your gift will determine the renewal date and when subsequent gifts are
              processed, depending on your preferred payment method
            </div>
          </template>
        </tooltip>
      </div>

      <div v-if="sessionStore.donationForm.delayStart">
        <vue-date-picker
          v-model="sessionStore.donationForm.delayStartDate"
          class="vue-date-picker"
          :enable-time-picker="false"
          placeholder="Select start date"
          :max-date="calendarMax"
          :min-date="calendarMin"
          prevent-min-max-navigation
          auto-apply
        />
        <span
          v-if="formErrors.date"
          class="form-error"
        >
          {{ formErrors.date }}
        </span>
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/styles/_variables.scss';


.donor-info-form-container {
  @apply p-4 mt-4 border border-slate-500 rounded-md;

  @screen md {
    @apply p-8 mt-10;
  }
}

.toggle-container {
  @apply flex flex-row items-center;

  .toggle-switch {
    min-width: 60px;
  }
}

// Inputs
button {
  @apply w-full rounded font-semibold text-lg;

  background-color: var(--button-color);
  color: var(--button-text);

  &:hover {
    background-color: var(--button-hover);
  }
}

input, :deep(input), select {
  @apply bg-white text-black border border-gray-300 disabled:bg-default_white disabled:bg-opacity-0;
  @apply w-full px-4 py-3 text-lg leading-normal rounded-lg shadow-sm focus:outline-none max-h-12;

  &:focus {
    border: 1px solid var(--input-focus)
  }
}

.vue-date-picker {
  max-width: 300px;
  :deep(input) {
    padding-left: 32px; // Fix for weird overlapping issue from library
  }
}

label {
  color: black;
}

.form-error {
  @apply text-sm text-red-500 font-semibold;
}
</style>
